import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { projectAPI, caseAPI } from '../services/api';
import { Box, Typography, Paper, Divider, Button, CircularProgress, Alert, Breadcrumbs, Link, TableContainer, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import { Home as HomeIcon, Folder as ProjectIcon, Business as BusinessIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import ProjectActivitiesManager from '../components/ProjectActivitiesManager';

// Helper function to format case status to human-readable label
const getStatusLabel = (status: string): string => {
  if (!status) return 'N/A';
  
  switch (status.toLowerCase()) {
    case 'open':
      return 'Open';
    case 'in_progress':
      return 'In Progress';
    case 'completed':
      return 'Completed';
    case 'on_hold':
      return 'On Hold';
    case 'cancelled':
      return 'Cancelled';
    case 'awaiting_info':
      return 'Awaiting Info';
    case 'closed':
      return 'Closed';
    case 'estimate_approved':
      return 'Estimate Approved';
    case 'estimate_declined':
      return 'Estimate Declined';
    case 'estimate_sent':
      return 'Estimate Sent';
    case 'mrr_completed':
      return 'MRR Completed';
    case 'mrr_delivered':
      return 'MRR Delivered';
    case 'na':
      return 'N/A';
    case 'pending':
      return 'Pending';
    case 'quality_check':
      return 'Quality Check';
    case 'quality_error':
      return 'Quality Error';
    case 'reworked':
      return 'Reworked';
    case 'training':
      return 'Training';
    case 'voicemail':
      return 'Voicemail';
    case 'wip':
      return 'WIP';
    case 'delivered':
      return 'Delivered';
    default:
      // Convert snake_case to Title Case
      return status
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
  }
};

const ProjectView: React.FC = () => {
  const { projectId, firmId } = useParams<{ projectId: string; firmId?: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [project, setProject] = useState<any>(null);
  const [recentCases, setRecentCases] = useState<any[]>([]);
  const { user } = useAuth();
  const isPmpAdmin = () => {
    if (!user?.roles) return false;
    return user.roles.some(role => role.slug === 'pmp_admin');
  };

  const isSiteAdmin = () => {
    if (!user?.roles) return false;
    return user.roles.some(role => role.slug === 'site_admin');
  };

  const canEdit = isPmpAdmin() || isSiteAdmin();

  useEffect(() => {
    const load = async () => {
      try {
        const res = await projectAPI.getById(Number(projectId));
        const proj = res.data.data;
        setProject(proj);
        // If route not firm-scoped, redirect to canonical firms/:firmId/projects/:projectId when firm id is available
        if (!firmId && proj.firm_id) {
          navigate(`/firms/${proj.firm_id}/projects/${proj.id}`, { replace: true });
          return;
        }
        // Load recent cases for this project
        const casesRes = await caseAPI.getAll({ project_id: proj.id, per_page: 5 });
        const list = casesRes.data.data?.data || casesRes.data.data || [];
        setRecentCases(list.slice(0, 5));
      } catch (e: any) {
        setError(e.message || 'Failed to load project');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [projectId, firmId, navigate]);

  if (loading) return <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!project) return <Alert severity="warning">Project not found</Alert>;

  const custom = project.settings?.custom_fields || {};

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          color="inherit"
          href="#"
          onClick={(e) => { e.preventDefault(); navigate('/firms'); }}
          sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
        >
          <HomeIcon sx={{ mr: 1 }} />
          Firms
        </Link>
        {project.firm_id && (
          <Link
            color="inherit"
            href="#"
            onClick={(e) => { e.preventDefault(); navigate(`/firms/${project.firm_id}`); }}
            sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
          >
            <BusinessIcon sx={{ mr: 1 }} />
            {project.firm?.name || `Firm #${project.firm_id}`}
          </Link>
        )}
        <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
          <ProjectIcon sx={{ mr: 1 }} />
          {project.name}
        </Typography>
      </Breadcrumbs>
      <Typography variant="h4" gutterBottom>{project.name}</Typography>
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <Button variant="outlined" onClick={() => navigate(-1)}>Back</Button>
        {canEdit && (
          <Button variant="contained" onClick={() => navigate(`/firms/${project.firm_id}/projects/${project.id}/edit`)}>Edit Project</Button>
        )}
        <Button variant="outlined" onClick={() => navigate(`/firms/${project.firm_id}/cases/new`)}>New Case</Button>
      </Box>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ color: 'primary.main' }}>Description</Typography>
        <Divider sx={{ my: 1 }} />
        <div dangerouslySetInnerHTML={{ __html: project.description }} />

        <Typography variant="h6" sx={{ mt: 3, color: 'primary.main' }}>Settings</Typography>
        <Divider sx={{ my: 1 }} />
        <Typography variant="body2">Billing Type: {custom.billing_type || '-'}</Typography>
        <Typography variant="body2">Rate: {custom.rate ?? '-'}</Typography>
        <Typography variant="body2">Free cases: {custom.free_cases_allowed ?? '-'}</Typography>
        <Typography variant="body2">Hide Case Timer: {custom.hide_case_timer ? 'Yes' : 'No'}</Typography>

        {custom.enable_checklist && (
          <>
            <Typography variant="h6" sx={{ mt: 3, color: 'primary.main' }}>Checklist</Typography>
            <Divider sx={{ my: 1 }} />
            <div dangerouslySetInnerHTML={{ __html: custom.checklist_instructions || '' }} />
          </>
        )}

        {custom.enable_disposition && (
          <>
            <Typography variant="h6" sx={{ mt: 3, color: 'primary.main' }}>Disposition</Typography>
            <Divider sx={{ my: 1 }} />
            <Typography variant="body2">Mandatory: {custom.mandatory_disposition_field ? 'Yes' : 'No'}</Typography>
            <Typography variant="body2">Options:</Typography>
            <ul>
              {(custom.disposition_list || []).map((d: string) => (<li key={d}>{d}</li>))}
            </ul>
          </>
        )}
      </Paper>

      {project.settings && (project.settings.estimate_type === 'hours' || project.settings.estimate_type === 'both') && (
        <ProjectActivitiesManager
          projectId={project.id}
          isTimeBased={true}
        />
      )}

      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" sx={{ color: 'secondary.main' }}>Recent Cases</Typography>
        <Divider sx={{ my: 1 }} />
        {recentCases.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No cases found for this project.</Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentCases.map((c) => {
                  const caseFirmId = c.firm_id || project.firm_id;
                  const caseLink = caseFirmId ? `/firms/${caseFirmId}/cases/${c.id}` : null;
                  
                  return (
                    <TableRow key={c.id} hover>
                      <TableCell>
                        {caseLink ? (
                          <Link
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              navigate(caseLink);
                            }}
                            sx={{ 
                              color: 'primary.main', 
                              textDecoration: 'none',
                              '&:hover': { textDecoration: 'underline' }
                            }}
                          >
                            #{c.id}
                          </Link>
                        ) : (
                          `#${c.id}`
                        )}
                      </TableCell>
                      <TableCell>
                        {caseLink ? (
                          <Link
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              navigate(caseLink);
                            }}
                            sx={{ 
                              color: 'primary.main', 
                              textDecoration: 'none',
                              '&:hover': { textDecoration: 'underline' }
                            }}
                          >
                            {c.title || c.name}
                          </Link>
                        ) : (
                          c.title || c.name
                        )}
                      </TableCell>
                      <TableCell>{getStatusLabel(c.status)}</TableCell>
                      <TableCell>{new Date(c.created_at).toLocaleString()}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default ProjectView;
